import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  IconAlertCircle, IconChevronLeft, IconStar, IconCalendar, IconPhone,
  IconMail, IconCake,
} from '@tabler/icons-react';
import AdminLayout from '../../components/layout/AdminLayout';

/**
 * Phase 7 — Customer detail. Loyalty + history only.
 * House accounts are NO LONGER tied to customers (admin internal groups
 * live in /admin/accounts).
 */

interface ApiCustomer {
  id: string;
  name: string;
  phone: string;
  email: string | null;
  memberNumber: string;
  dob: string | null;
  loyaltyPoints: number;
  totalSpentCents: number;
  lastVisitAt: string | null;
  createdAt: string;
}

function fmtR(cents: number) {
  return `R ${(cents / 100).toFixed(2)}`;
}

export default function AdminCustomerDetailPage() {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [customer, setCustomer] = useState<ApiCustomer | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    try {
      const r = await fetch(`/api/customers/${id}`, { credentials: 'include' });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = await r.json();
      setCustomer(data.customer);
      setError(null);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void load(); /* eslint-disable-next-line */ }, [id]);

  if (loading || !customer) {
    return <AdminLayout title="Customer"><p className="text-13 italic text-gray-700">Loading…</p></AdminLayout>;
  }

  return (
    <AdminLayout
      title={customer.name}
      subtitle={`Member ${customer.memberNumber}`}
      actions={
        <button
          type="button"
          onClick={() => navigate('/admin/customers')}
          className="h-10 px-4 inline-flex items-center gap-2 rounded-md bg-light border border-border text-13 font-bold text-heading hover:bg-gray-100"
        >
          <IconChevronLeft size={14} stroke={2.5} />
          All customers
        </button>
      }
    >
      {error && (
        <div className="rounded-md bg-danger-soft border border-danger/30 p-3 flex items-start gap-2 mb-5">
          <IconAlertCircle size={18} className="text-danger flex-shrink-0 mt-0.5" />
          <p className="text-13 font-semibold text-danger">{error}</p>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-5">
        <section className="lg:col-span-2 rounded-lg bg-surface border border-border p-5">
          <h2 className="text-13 font-bold uppercase tracking-wider text-gray-700 mb-3">Contact</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <InfoRow Icon={IconPhone} label="Phone" value={customer.phone || '—'} />
            <InfoRow Icon={IconMail} label="Email" value={customer.email || '—'} />
            <InfoRow Icon={IconCake} label="Date of birth" value={customer.dob ? new Date(customer.dob).toLocaleDateString('en-ZA', { day: 'numeric', month: 'long', year: 'numeric' }) : '—'} />
            <InfoRow Icon={IconCalendar} label="Joined" value={new Date(customer.createdAt).toLocaleDateString('en-ZA', { day: 'numeric', month: 'long', year: 'numeric' })} />
          </div>
        </section>

        <section className="rounded-lg bg-surface border border-border p-5">
          <h2 className="text-13 font-bold uppercase tracking-wider text-gray-700 mb-3">Loyalty</h2>
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 rounded-md bg-warning-soft border border-warning/30">
              <span className="inline-flex items-center gap-2 text-13 font-bold text-warning">
                <IconStar size={14} stroke={2} fill="currentColor" />
                Points
              </span>
              <span className="text-20 font-bold text-warning tabular-nums">{customer.loyaltyPoints}</span>
            </div>
            <div className="flex items-center justify-between p-3 rounded-md bg-light border border-border-soft">
              <span className="text-13 font-bold text-heading">Total spent</span>
              <span className="text-16 font-bold text-heading tabular-nums">{fmtR(customer.totalSpentCents)}</span>
            </div>
            <p className="text-11 text-gray-700">
              Tier engine + rewards ship in Phase 8.
            </p>
          </div>
        </section>
      </div>

      {/* Order history */}
      <CustomerOrderHistory customerId={customer.id} />
    </AdminLayout>
  );
}

function InfoRow({ Icon, label, value }: { Icon: typeof IconPhone; label: string; value: string }) {
  return (
    <div className="flex items-center gap-3 p-3 rounded-md bg-light border border-border-soft">
      <Icon size={16} className="text-gray-700 flex-shrink-0" />
      <div className="min-w-0">
        <p className="text-11 font-bold uppercase tracking-wider text-gray-700">{label}</p>
        <p className="text-14 font-semibold text-heading truncate">{value}</p>
      </div>
    </div>
  );
}

interface OrderRow {
  id: string;
  orderNumber: number;
  status: string;
  placedAt: string;
  totalCents: number;
  channel: string;
  cashier: { name: string };
  payments: { method: string }[];
  _count: { lines: number };
}

function CustomerOrderHistory({ customerId }: { customerId: string }) {
  const navigate = useNavigate();
  const [orders, setOrders] = useState<OrderRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`/api/orders/admin?customerId=${encodeURIComponent(customerId)}&pageSize=50`, { credentials: 'include' });
        if (!r.ok) return;
        const data = await r.json();
        if (!cancelled) setOrders(data.orders ?? []);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [customerId]);

  return (
    <section className="rounded-lg bg-surface border border-border p-5 mb-5">
      <h2 className="text-13 font-bold uppercase tracking-wider text-gray-700 mb-3">Order history</h2>
      {loading ? (
        <p className="text-13 italic text-gray-700">Loading…</p>
      ) : orders.length === 0 ? (
        <p className="text-13 italic text-gray-700">No orders yet.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-13">
            <thead>
              <tr className="text-left text-12 font-bold uppercase tracking-wider text-gray-700">
                <th className="py-2 pr-3">#</th>
                <th className="py-2 pr-3">When</th>
                <th className="py-2 pr-3">Status</th>
                <th className="py-2 pr-3">Cashier</th>
                <th className="py-2 pr-3 text-right">Lines</th>
                <th className="py-2 pr-3 text-right">Total</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => (
                <tr
                  key={o.id}
                  onClick={() => navigate(`/admin/orders?q=${o.orderNumber}`)}
                  className="border-t border-border-soft cursor-pointer hover:bg-light"
                >
                  <td className="py-2 pr-3 font-bold text-heading tabular-nums">#{o.orderNumber}</td>
                  <td className="py-2 pr-3 text-gray-700 tabular-nums">{new Date(o.placedAt).toLocaleString('en-ZA', { dateStyle: 'short', timeStyle: 'short' })}</td>
                  <td className="py-2 pr-3 text-gray-700">{o.status.replace(/_/g, ' ')}</td>
                  <td className="py-2 pr-3 text-gray-700">{o.cashier.name}</td>
                  <td className="py-2 pr-3 text-right text-gray-700 tabular-nums">{o._count.lines}</td>
                  <td className="py-2 pr-3 text-right font-bold text-heading tabular-nums">{fmtR(o.totalCents)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
